/**
 * "Open legally and stay inspection-ready" (docs/ONBOARDING.md steps 1–4), web side. The business types and the gate
 * come from core opening.ts; this file adds step 2 ("Where are you?"). The answer is stored on the location (Location.openingPhase,
 * migration 0065), so web, tablet and phone tell the same truth (persona review 2026-10-01 A8).
 */
import { businessSetupFor, legacyPhase, type BusinessStage, type PhaseInput, type UsState } from "@logwave/core";

export { BUSINESS_TYPES, NOT_LEGAL_ADVICE, businessType, openingGate } from "@logwave/core";
export type { BusinessType, OpeningGate, OpeningStep } from "@logwave/core";

/* ─────────────── step 2: where are you? ─────────────── */

export type OpeningPhase = "planning" | "opening_soon" | "open";
export const PHASES: { id: OpeningPhase; label: string; hint: string }[] = [
  { id: "planning", label: "Planning", hint: "No place or truck yet" },
  { id: "opening_soon", label: "Opening soon", hint: "I have a place and a date" },
  { id: "open", label: "Already open", hint: "I'm serving food now" },
];
/**
 * Where the place is, as the Open legally screens show it (three ways), from the location's own stage (the server's;
 * core journey.ts legacyPhase), the same on every device.
 */
export const phaseOf = (loc: { openingPhase?: BusinessStage }): OpeningPhase | undefined => legacyPhase(loc.openingPhase);
/** A device's older answer is still a valid thing to send: the server maps it (core journey.ts stageFromPhase). */
export type { PhaseInput };

/**
 * Before migration 0065 the answer was kept in this browser only. It is read once to copy it to the server (the
 * backfill, Opening.tsx usePhaseBackfill), then cleared. Nothing else reads it.
 */
const PHASE_KEY = (orgId: string) => `logwave.phase.${orgId}`;
export function loadLegacyPhase(orgId: string): OpeningPhase | undefined {
  try { const v = localStorage.getItem(PHASE_KEY(orgId)); return PHASES.some((p) => p.id === v) ? (v as OpeningPhase) : undefined; } catch { return undefined; }
}
export function clearLegacyPhase(orgId: string) { try { localStorage.removeItem(PHASE_KEY(orgId)); } catch { /* ignore */ } }

/**
 * Roadmap steps for the first hire (core setup.ts phase "hire"); the rest are steps to open. The place's own state's
 * roadmap: Utah's hire steps (workers' comp, UI account, the county permit…) are Utah's ids; another state's are its own.
 */
const HIRE_IDS = new Map<UsState, Set<string>>();
export const isHireStep = (id: string, state: UsState) => {
  let ids = HIRE_IDS.get(state);
  if (!ids) HIRE_IDS.set(state, (ids = new Set(businessSetupFor(state).filter((s) => s.phase === "hire").map((s) => s.id))));
  return ids.has(id);
};
